import { useMemo } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Box, Avatar, Typography, Stack, IconButton, Badge, Tooltip,
  BottomNavigation, BottomNavigationAction, useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  HomeRounded as HomeIcon,
  AccountBalanceWalletRounded as AccountIcon,
  SchoolRounded as AcademicIcon,
  PaymentsRounded as FinanceIcon,
  PersonRounded as ProfileIcon,
  LogoutRounded as LogoutIcon,
  NotificationsRounded as BellIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { P, PAGE_BG, RAIL_BG, RAIL, CARD_SHADOW } from './portalTheme';

// ─────────────────────────────────────────────────────────────────
// PortalLayout — one portal, three widths.
//
// The first version pinned everything to a 480px column at EVERY size,
// which on a laptop is a phone-shaped strip marooned in the middle of a
// large screen. A phone layout is what a phone needs; it is not what
// "the design" is. So the shell adapts:
//
//   PHONE  (< 600px)   the app shell — welcome row, one column, bottom
//                      bar under the thumb.
//   TABLET (600–900)   same shell, wider column, still bottom bar. This
//                      is the size that genuinely reads as an app.
//   DESKTOP (900px +)  a real web page: a persistent left rail, a wide
//                      content area, and pages free to lay out in two
//                      or three columns because there is room for it.
//
// The nav ITEMS never change, only where they sit — so a parent who
// learns the portal on a phone finds the same five things on a laptop.
// ─────────────────────────────────────────────────────────────────

export const PORTAL_NAV = [
  { key: 'home',      label: 'Home',     path: '/portal',           icon: <HomeIcon /> },
  { key: 'account',   label: 'Account',  path: '/portal/account',   icon: <AccountIcon /> },
  { key: 'academics', label: 'Academic', path: '/portal/academics', icon: <AcademicIcon /> },
  { key: 'finance',   label: 'Finance',  path: '/portal/finance',   icon: <FinanceIcon /> },
  { key: 'profile',   label: 'Profile',  path: '/portal/profile',   icon: <ProfileIcon /> },
];

function RoundAction({ title, onClick, children }) {
  return (
    <Tooltip title={title}>
      <IconButton onClick={onClick} sx={{
        width: 42, height: 42,
        border: '1px solid', borderColor: P.line,
        bgcolor: 'rgba(255,255,255,.86)', color: P.ink,
        backdropFilter: 'blur(6px)',
        boxShadow: CARD_SHADOW,
        transition: 'background-color .15s, border-color .15s, transform .15s',
        '&:hover': {
          bgcolor: P.brandSoft, borderColor: P.brand, transform: 'translateY(-1px)',
        },
      }}>
        {children}
      </IconButton>
    </Tooltip>
  );
}

// The desktop rail. A row of tabs across the top would work too, but a
// rail leaves the full page width for content and keeps the five
// destinations visible while the parent reads — which is the point of
// having the room in the first place.
function SideRail({ active, onGo, onLogout }) {
  return (
    <Box sx={{
      width: 236, flexShrink: 0, position: 'sticky', top: 0, alignSelf: 'flex-start',
      height: '100vh', background: RAIL_BG,
      // A cast shadow rather than a border. On a dark rail against a
      // light page a 1px rule reads as a seam; a shadow reads as the
      // rail sitting in front.
      boxShadow: '6px 0 24px -14px rgba(13,71,161,.55)',
      display: 'flex', flexDirection: 'column', py: 2.5, px: 1.6, zIndex: 2,
    }}>
      <Typography sx={{
        fontSize: 11, fontWeight: 800, letterSpacing: 1.4, textTransform: 'uppercase',
        color: RAIL.label, px: 1.4, mb: 2.2,
      }}>
        Parent Portal
      </Typography>
      <Stack spacing={0.4} sx={{ flex: 1 }}>
        {PORTAL_NAV.map(n => {
          const on = active.key === n.key;
          return (
            <Stack key={n.key} direction="row" alignItems="center" spacing={1.4}
              onClick={() => onGo(n.path)}
              sx={{
                position: 'relative',
                px: 1.4, py: 1.25, borderRadius: 2.5, cursor: 'pointer',
                bgcolor: on ? RAIL.active : 'transparent',
                color: on ? RAIL.textActive : RAIL.text,
                fontWeight: on ? 800 : 600,
                boxShadow: on ? 'inset 0 0 0 1px rgba(255,255,255,.22)' : 'none',
                transition: 'background-color .18s, color .18s',
                // A short white bar on the leading edge of the active
                // item. Colour alone is a weak signal at this size, and
                // on a single-hue rail it is weaker still.
                '&::before': on ? {
                  content: '""', position: 'absolute', left: 0, top: '50%',
                  transform: 'translateY(-50%)',
                  width: 3, height: 20, borderRadius: 3, bgcolor: RAIL.activeEdge,
                } : {},
                '&:hover': {
                  bgcolor: on ? RAIL.active : RAIL.hover,
                  color: RAIL.textActive,
                },
              }}>
              <Box sx={{ display: 'flex' }}>{n.icon}</Box>
              <Typography sx={{ fontSize: 14.5, fontWeight: 'inherit' }}>{n.label}</Typography>
            </Stack>
          );
        })}
      </Stack>
      <Stack direction="row" alignItems="center" spacing={1.4}
        onClick={onLogout}
        sx={{
          px: 1.4, py: 1.2, borderRadius: 2.5, cursor: 'pointer', color: RAIL.text,
          transition: 'background-color .15s, color .15s',
          '&:hover': { bgcolor: 'rgba(255,180,166,.14)', color: RAIL.danger },
        }}>
        <LogoutIcon />
        <Typography sx={{ fontSize: 14.5, fontWeight: 600 }}>Sign out</Typography>
      </Stack>
    </Box>
  );
}

export default function PortalLayout() {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const location = useLocation();
  const nav = useNavigate();
  const { user, logout } = useAuth();

  const active = useMemo(() => {
    const matches = PORTAL_NAV.filter(n =>
      location.pathname === n.path || location.pathname.startsWith(n.path + '/'));
    if (!matches.length) return PORTAL_NAV[0];
    return matches.sort((a, b) => b.path.length - a.path.length)[0];
  }, [location.pathname]);

  const handleLogout = () => { logout(); nav('/login', { replace: true }); };

  const parent = user?.parent || {};
  // Greet the guardian where we know them — the person holding the
  // phone is the parent, not the child whose account it is.
  const greetName = (parent.guardian || '').trim()
    || (parent.student_name || '').trim() || 'there';
  const firstName = greetName.split(/\s+/)[0];
  const initials = greetName.split(/\s+/).slice(0, 2)
    .map(s => s[0]).filter(Boolean).join('').toUpperCase() || '?';

  const welcomeRow = (
    <Stack direction="row" alignItems="center" spacing={1.5}
      sx={{ pt: { xs: 2.4, md: 3 }, pb: { xs: 1.6, md: 2 } }}>
      <Avatar sx={{
        width: { xs: 46, md: 52 }, height: { xs: 46, md: 52 },
        bgcolor: P.brandSoft, color: P.brandDeep,
        fontWeight: 800, fontSize: { xs: 16, md: 18 },
        border: '1px solid', borderColor: P.line,
      }}>
        {initials}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 13, color: P.inkSoft, lineHeight: 1.2 }}>Welcome</Typography>
        <Typography noWrap sx={{
          fontSize: { xs: 19, md: 23 }, fontWeight: 800, color: P.ink, lineHeight: 1.2,
        }}>
          {firstName}
        </Typography>
      </Box>
      {parent.student_name && isDesktop && (
        <Box sx={{ textAlign: 'right', mr: 1 }}>
          <Typography sx={{ fontSize: 11, color: P.inkSoft, letterSpacing: .5,
            textTransform: 'uppercase', fontWeight: 700 }}>
            Viewing
          </Typography>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: P.ink }}>
            {parent.student_name}{parent.cla ? ` · ${parent.cla}` : ''}
          </Typography>
        </Box>
      )}
      <RoundAction title="Notifications">
        <Badge variant="dot" color="error" invisible>
          <BellIcon sx={{ fontSize: 21 }} />
        </Badge>
      </RoundAction>
      {/* Sign-out lives in the rail on desktop; repeating it here would
          be two buttons doing one job. */}
      {!isDesktop && (
        <RoundAction title="Sign out" onClick={handleLogout}>
          <LogoutIcon sx={{ fontSize: 21 }} />
        </RoundAction>
      )}
    </Stack>
  );

  // ── Desktop: rail + wide page ───────────────────────────────────
  if (isDesktop) {
    return (
      <Box sx={{
        minHeight: '100vh', display: 'flex',
        bgcolor: P.bg, background: PAGE_BG,
        backgroundAttachment: 'fixed',
      }}>
        <SideRail active={active} onGo={nav} onLogout={handleLogout} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ maxWidth: 1180, mx: 'auto', px: { md: 4, lg: 5 }, pb: 6 }}>
            {welcomeRow}
            <Outlet />
          </Box>
        </Box>
      </Box>
    );
  }

  // ── Phone and tablet: the app shell ─────────────────────────────
  return (
    <Box sx={{
      minHeight: '100vh', display: 'flex', justifyContent: 'center',
      bgcolor: P.bg, background: PAGE_BG, backgroundAttachment: 'fixed',
    }}>
      <Box sx={{
        width: '100%', maxWidth: 620, minHeight: '100vh',
        display: 'flex', flexDirection: 'column', position: 'relative',
        px: { xs: 2.2, sm: 3 },
      }}>
        {welcomeRow}
        <Box sx={{ flex: 1, pb: 11 }}>
          <Outlet />
        </Box>
        <BottomNavigation
          value={active.key}
          onChange={(_, v) => {
            const item = PORTAL_NAV.find(n => n.key === v);
            if (item) nav(item.path);
          }}
          showLabels
          sx={{
            position: 'fixed', bottom: 0, zIndex: 1100,
            width: '100%', maxWidth: 620, height: 68,
            // Frosted rather than solid, so content scrolling underneath
            // is felt rather than cut off at a hard line.
            bgcolor: 'rgba(255,255,255,.92)',
            backdropFilter: 'blur(12px)',
            borderTop: '1px solid', borderColor: P.line,
            boxShadow: '0 -4px 24px -8px rgba(27,26,23,.16)',
            '& .MuiBottomNavigationAction-root': { color: P.inkSoft, minWidth: 0, px: 0.5 },
            '& .MuiBottomNavigationAction-root.Mui-selected': { color: P.brandDeep },
            '& .MuiBottomNavigationAction-label': { fontSize: 11, fontWeight: 700 },
            '& .MuiBottomNavigationAction-label.Mui-selected': { fontSize: 11.5 },
          }}>
          {PORTAL_NAV.map(n => (
            <BottomNavigationAction key={n.key} value={n.key} label={n.label} icon={n.icon} />
          ))}
        </BottomNavigation>
      </Box>
    </Box>
  );
}
